import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Car, ArrowRight, ShieldCheck, AlertTriangle, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get("token") || "").trim(), [searchParams]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (password !== confirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password aggiornata!");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F6FA]">
      <div className="hidden lg:flex relative bg-gradient-to-br from-[#1A2A6C] via-[#2D3F8F] to-[#3D4FA0] p-12 items-center justify-center overflow-hidden text-white">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-40 -left-20 w-[28rem] h-[28rem] rounded-full bg-white/5 blur-2xl" />
        <div className="relative z-10 max-w-md anim-slide-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-xs font-semibold mb-6">
            <ShieldCheck className="w-3.5 h-3.5" /> Nuova password
          </div>
          <h1 className="font-heading text-5xl xl:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            Imposta la tua <span className="text-[#FF9E1B]">nuova password</span>.
          </h1>
          <p className="text-lg font-medium text-white/85 leading-relaxed">
            Scegli una password di almeno 6 caratteri. Da subito potrai accedere al tuo account Hopzigo.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md anim-slide-up">
          <Link to="/login" className="inline-flex items-center gap-2.5 mb-8" data-testid="reset-logo">
            <img src="/hopzigo-logo.png" alt="Hopzigo" className="h-12 w-auto" />
          </Link>

          {!token ? (
            <div className="brut-card p-6 sm:p-8 anim-pop-in" data-testid="reset-no-token">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 grid place-items-center rounded-2xl bg-[#FFE3E3] text-[#FF4D5E]">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <h2 className="font-heading text-2xl font-bold">Link non valido</h2>
              </div>
              <p className="text-[#525252] mb-6">Manca il token di reset nell'URL. Richiedi un nuovo link.</p>
              <Link to="/forgot-password" className="brut-btn w-full" data-testid="reset-request-new">
                Richiedi nuovo link <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">Imposta nuova password</h2>
              <p className="text-[#8A91A8] mb-8">Scegli una password sicura e confermala.</p>

              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <label className="brut-label" htmlFor="pwd">Nuova password</label>
                  <div className="relative">
                    <input
                      id="pwd"
                      type={showPwd ? "text" : "password"}
                      className="brut-input pr-12"
                      placeholder="Almeno 6 caratteri"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      autoFocus
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-lg text-[#8A91A8] hover:bg-[#F5F6FA]"
                      aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                      data-testid="reset-toggle-visibility"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="brut-label" htmlFor="confirm">Conferma password</label>
                  <input
                    id="confirm"
                    type={showPwd ? "text" : "password"}
                    className="brut-input"
                    placeholder="Ripeti la password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-confirm-input"
                  />
                </div>
                <button type="submit" className="brut-btn w-full text-base" disabled={loading} data-testid="reset-submit-button">
                  {loading ? "Aggiornamento…" : (<>Imposta password <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
